import React from 'react';
import Icon from '@/components/AppIcon';
import Button from '@/components/ui/Button';
import EmployeeAvatar from '@/components/employees/EmployeeAvatar';
import useTranslation from '@/hooks/useTranslation';
import formatTranslation from '@/utils/formatTranslation';

const RequestCard = ({
  request,
  onApprove,
  onDeny,
  onViewDetails,
  isCompact = false
}) => {
  const { t, language } = useTranslation(['timeOffManagement', 'common']);
  const translate = (key, fallback) => t(`timeOffManagement:${key}`, fallback);
  const locale = language || 'en-US';
  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-warning text-warning-foreground';
      case 'approved': return 'bg-success text-success-foreground';
      case 'denied': return 'bg-error text-error-foreground';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const getStatusLabel = (status) => {
    const fallback =
      status && typeof status === 'string'
        ? status.charAt(0).toUpperCase() + status.slice(1)
        : translate('statuses.pending', 'Pending');
    if (!status) {
      return translate('statuses.pending', 'Pending');
    }
    return translate(`statuses.${status.toLowerCase()}`, fallback);
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'vacation': return 'Palmtree';
      case 'sick': return 'Heart';
      case 'personal': return 'User';
      case 'emergency': return 'AlertTriangle';
      default: return 'Calendar';
    }
  };

  const getTypeLabel = (type) => {
    if (!type) {
      return translate('types.default', 'Absence');
    }
    return translate(`types.${type}`, type);
  };

  const formatDateRange = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const options = { month: 'short', day: 'numeric' };
    
    if (start?.toDateString() === end?.toDateString()) {
      return start?.toLocaleDateString(locale, { ...options, year: 'numeric' });
    }
    
    return `${start?.toLocaleDateString(locale, options)} - ${end?.toLocaleDateString(locale, options)}`;
  };

  const formatDurationLabel = (duration) => {
    const count = typeof duration === 'number' ? duration : 0;
    return formatTranslation(
      translate(
        `duration.days.${count === 1 ? 'singular' : 'plural'}`,
        count === 1 ? '{{count}} day' : '{{count}} days',
      ),
      { count },
    );
  };

  const employeeName = request?.employee?.name ?? translate('card.unknownName', 'Employee');
  const avatarAlt = formatTranslation(
    translate('card.avatarAlt', '{{name}} profile photo'),
    { name: employeeName },
  );

  if (isCompact) {
    return (
      <div className="bg-card border border-border rounded-lg p-4 hover:shadow-elevation-2 transition-smooth">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center space-x-3">
            <EmployeeAvatar
              url={request?.employee?.avatarUrl}
              name={request?.employee?.name}
              alt={avatarAlt}
              status={request?.employee?.status}
              size="sm"
              showStatus={false}
              wrapperClassName="border border-border"
            />
            <div>
              <h4 className="font-medium text-foreground">{request?.employee?.name}</h4>
              <p className="text-sm text-muted-foreground">{request?.employee?.position}</p>
            </div>
          </div>
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(request?.status)}`}>
            {getStatusLabel(request?.status)}
          </span>
        </div>
        <div className="space-y-2 mb-4">
          <div className="flex items-center text-sm text-foreground">
            <Icon name={getTypeIcon(request?.type)} size={16} className="mr-2 text-muted-foreground" />
            <span className="capitalize">{getTypeLabel(request?.type)}</span>
            <span className="mx-2 text-muted-foreground">•</span>
            <span>{formatDateRange(request?.startDate, request?.endDate)}</span>
          </div>
          <div className="flex items-center text-sm text-muted-foreground">
            <Icon name="Clock" size={16} className="mr-2" />
            <span>{formatDurationLabel(request?.duration)}</span>
          </div>
        </div>
        {request?.status === 'pending' && (
          <div className="flex space-x-2">
            <Button
              variant="success"
              size="sm"
              onClick={() => {
                onApprove(request?.id)?.catch((error) =>
                  console.error('Failed to approve absence request', error),
                );
              }}
              className="flex-1"
            >
              <Icon name="Check" size={16} className="mr-1" />
              {translate('actions.approve', 'Approve')}
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={() => {
                onDeny(request?.id)?.catch((error) =>
                  console.error('Failed to deny absence request', error),
                );
              }}
              className="flex-1"
            >
              <Icon name="X" size={16} className="mr-1" />
              {translate('actions.deny', 'Deny')}
            </Button>
          </div>
        )}
      </div>
    );
  }

  return (
    <tr className="border-b border-border hover:bg-muted/50 transition-smooth">
      <td className="px-6 py-4">
        <div className="flex items-center space-x-3">
          <EmployeeAvatar
            url={request?.employee?.avatarUrl}
            name={request?.employee?.name}
            alt={avatarAlt}
            status={request?.employee?.status}
            size="sm"
            showStatus={false}
            wrapperClassName="border border-border"
          />
          <div>
            <div className="font-medium text-foreground">{request?.employee?.name}</div>
            <div className="text-sm text-muted-foreground">{request?.employee?.position}</div>
          </div>
        </div>
      </td>
      <td className="px-6 py-4">
        <div className="flex items-center">
          <Icon name={getTypeIcon(request?.type)} size={16} className="mr-2 text-muted-foreground" />
          <span className="capitalize text-foreground">{getTypeLabel(request?.type)}</span>
        </div>
      </td>
      <td className="px-6 py-4 text-foreground">
        {formatDateRange(request?.startDate, request?.endDate)}
      </td>
      <td className="px-6 py-4 text-foreground">
        {formatDurationLabel(request?.duration)}
      </td>
      <td className="px-6 py-4">
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(request?.status)}`}>
          {getStatusLabel(request?.status)}
        </span>
      </td>
      <td className="px-6 py-4">
        <div className="flex items-center space-x-2">
          {request?.status === 'pending' && (
            <>
              <Button
                variant="success"
                size="sm"
                onClick={() => {
                  onApprove(request?.id)?.catch((error) =>
                    console.error('Failed to approve absence request', error),
                  );
                }}
              >
                <Icon name="Check" size={16} />
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={() => {
                  onDeny(request?.id)?.catch((error) =>
                    console.error('Failed to deny absence request', error),
                  );
                }}
              >
                <Icon name="X" size={16} />
              </Button>
            </>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onViewDetails(request)}
          >
            <Icon name="Eye" size={16} />
          </Button>
        </div>
      </td>
    </tr>
  );
};

export default RequestCard;
